import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTrigger } from 'shared/components/ui/dialog';
import { ProductListResponseItem } from 'types/ProductListTypes';
import { ProductListTable } from './ProductListTable';
import { ProductSearchInput } from './ProductSearchInput';
import { RenameListModal } from './RenameListModal';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { ConstraintsService } from 'shared/services/ConstraintsService';
import { useToast } from 'shared/components/ui/use-toast';
import activityPanelStore from 'shared/store/activityPanelStore';
import finecoLogo from 'shared/assets/images/fineco.png';

import styles from './ProductListDetailModal.module.scss';
import { FinecoButton } from 'pages/assistant/components/FinecoButton';
import { createLogger } from 'shared/logger';

const log = createLogger('ProductList');

type ProductListDetailModalProps = {
  list: ProductListResponseItem;
  children: React.ReactNode;
};

export const ProductListDetailModal: React.FC<ProductListDetailModalProps> = ({ list, children }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [currentList, setCurrentList] = useState(list);
  const [updating, setUpdating] = useState(false);

  const isReadOnly = currentList.readonly;

  const handleAddProduct = async (isin: string) => {
    if (isReadOnly || updating) return;

    const isins = currentList.products.map((p) => p.isin);
    if (isins.includes(isin)) {
      return; // Already in list
    }

    setUpdating(true);
    try {
      const updatedList = await ConstraintsService.updateProductList(currentList.id, {
        isins: [...isins, isin],
      });
      setCurrentList(updatedList);

      // Update store
      const productLists = activityPanelStore.getState().productLists;
      const updatedLists = productLists.map((l) => (l.id === updatedList.id ? updatedList : l));
      activityPanelStore.setState({ productLists: updatedLists });

      const addedName = updatedList.products.find((p) => p.isin === isin)?.nome ?? isin;
      toast({
        title: t('global.success'),
        description: t('instrumentLists.addProductSuccess', { name: addedName }),
        duration: 4000,
      });
    } catch (error: any) {
      log.error('add product to list failed', { listId: currentList.id, isin, error });
      toast({
        title: t('global.error'),
        description: error?.response?.data?.detail || t('global.errorOccurred'),
        variant: 'destructive',
        duration: 4000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteProduct = async (isin: string) => {
    if (isReadOnly || updating) return;

    // Capture the product's display name from the pre-mutation state — once we
    // filter it out we lose access to its `nome`.
    const removedName = currentList.products.find((p) => p.isin === isin)?.nome ?? isin;

    setUpdating(true);
    try {
      const isins = currentList.products.map((p) => p.isin).filter((i) => i !== isin);
      const updatedList = await ConstraintsService.updateProductList(currentList.id, { isins });
      setCurrentList(updatedList);

      // Update store
      const productLists = activityPanelStore.getState().productLists;
      const updatedLists = productLists.map((l) => (l.id === updatedList.id ? updatedList : l));
      activityPanelStore.setState({ productLists: updatedLists });

      toast({
        title: t('global.success'),
        description: t('instrumentLists.removeProductSuccess', { name: removedName }),
        duration: 4000,
      });
    } catch (error: any) {
      log.error('remove product from list failed', { listId: currentList.id, isin, error });
      toast({
        title: t('global.error'),
        description: error?.response?.data?.detail || t('global.errorOccurred'),
        variant: 'destructive',
        duration: 4000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleRenameList = async (newName: string) => {
    if (isReadOnly) return;

    setUpdating(true);
    try {
      const updatedList = await ConstraintsService.updateProductList(currentList.id, { name: newName });
      setCurrentList(updatedList);

      // Update store
      const productLists = activityPanelStore.getState().productLists;
      const updatedLists = productLists.map((l) => (l.id === updatedList.id ? updatedList : l));
      activityPanelStore.setState({ productLists: updatedLists });

      toast({
        title: t('global.success'),
        description: t('instrumentLists.renameListSuccess', { name: newName }),
        duration: 4000,
      });
    } catch (error: any) {
      log.error('rename list failed', { listId: currentList.id, error });
      toast({
        title: t('global.error'),
        description: error?.response?.data?.detail || t('global.errorOccurred'),
        variant: 'destructive',
        duration: 4000,
      });
      throw error;
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteList = async () => {
    if (isReadOnly) return;

    setUpdating(true);
    try {
      await ConstraintsService.deleteProductList(currentList.id);

      toast({
        title: t('global.success'),
        description: t('instrumentLists.deleteListSuccess'),
      });

      // Reload lists and close modal
      activityPanelStore.getState().fetchProductLists();
      setOpen(false);
    } catch (error) {
      log.error('delete list failed', { listId: currentList.id, error });
      toast({
        title: t('global.error'),
        description: t('global.errorOccurred'),
        variant: 'destructive',
      });
      throw error;
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className={styles.dialogContent} showHeader={false}>
        <div className={styles.customHeader}>
          <div className={styles.headerLogo}>
            <img src={finecoLogo} alt="Fineco" width={32} height={32} />
          </div>
          <h2 className={styles.headerTitle}>{currentList.name}</h2>
          <div className={styles.headerSpacer} />
        </div>

        <div className={styles.modalBody}>
          {isReadOnly && <div className={styles.readOnlyBanner}>{t('instrumentLists.readOnlyList')}</div>}

          {!isReadOnly && (
            <>
              <span className={styles.searchLabel}>{t('instrumentLists.searchLabel')}</span>
              <ProductSearchInput onSelectProduct={handleAddProduct} disabled={updating} />
            </>
          )}

          <ProductListTable
            products={currentList.products}
            onDeleteProduct={!isReadOnly ? handleDeleteProduct : undefined}
            isEditable={!isReadOnly}
          />
        </div>

        {!isReadOnly && (
          <div className={styles.modalFooter}>
            <ConfirmDeleteModal listName={currentList.name} onConfirm={handleDeleteList}>
              <FinecoButton
                className={styles.deleteButton}
                click={() => {}}
                copy={t('instrumentLists.deleteList')}
                disabled={updating}
              />
            </ConfirmDeleteModal>
            <RenameListModal currentName={currentList.name} onRename={handleRenameList}>
              <FinecoButton
                className={styles.renameButton}
                click={() => {}}
                copy={t('instrumentLists.renameList')}
                disabled={updating}
              />
            </RenameListModal>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
